import type { Matcher } from 'react-day-picker';

import { TooltipProps as TooltipPropsM } from '@mui/material/Tooltip';

import DatePickerV3 from '../DatePickerV3/DatePicker.jsx';

export interface DatepickerProps {
  /**
   * If true calendar is open
   * @deprecated use isOpen instead with new DatePicker
   */
  open?: boolean;
  /**
   * If true calendar is open
   */
  isOpen?: boolean;
  /**
   * Callback for calendar opening
   * @deprecated
   */
  onCalendarOpen?: () => void;
  /**
   * Callback for calendar closing
   * @deprecated use onApply instead
   */
  onCalendarClose?: () => void;
  /**
   * The first day of the week
   * @deprecated
   */
  calendarStartDay?: number;
  /**
   * if true calendar should be close on select the date
   * @deprecated
   */
  shouldCloseOnSelect?: boolean;

  /**
   * The selected date
   */
  selected?: Date | null;
  /**
   * Callback when state is changing
   * @deprecated use onApply instead
   */
  onChange: (date: Date | null) => void;
  /**
   * Additional class name for the component
   */
  className?: string;

  /**
   * Array of dates to mark events
   */
  eventDates?: Date[];
  /**
   * Color of the input
   */
  color?: 'primary' | 'secondary';
  /**
   * Label of the input
   */
  label?: string;
  /**
   * Props fot input element to make it fullWidth
   */
  fullWidth?: boolean;
  /**
   * The helperText of the input. It is only used for layout. The actual labelling is handled by HelperText.
   */
  helperText?: string;
  /**
   * Handler that fires when the datepickerRange closes
   */
  onApply?: (date?: Date | null) => void;
  /**
   * If true, the datepicker will be in error status
   */
  error?: boolean;
  /**
   * The size can be small or medium
   * @default medium
   */
  size?: 'small' | 'medium';
  /**
   * The info text of the input
   */
  infoText?: string;
  /**
   * If true, the datepicker will be disabled
   */
  disabled?: boolean;
  /**
   * If true, the datepicker will be in warning status
   */
  warning?: boolean;
  /**
   * If true, the datepicker will be readOnly
   */
  readOnly?: boolean;
  /**
   * Props for the info text tooltip
   */
  InfoTextTooltipProps?: Partial<TooltipPropsM>;
  /**
   * The end adornment for the input
   * @deprecated
   */
  endAdornment?: React.ReactNode;
  /**
   * The placement of the datepicker
   */
  placement?: 'bottom-start' | 'bottom-end';
  /**
   * onFocus event handler for the input
   *   * @description Use `onApply` instead
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * onBlur event handler for the input
   *  * @description Use `onCancel` instead
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * @deprecated use disabledDates instead
   * disabledDates supports Matcher
   */
  minDate?: Date | null;
  /**
   * @deprecated use disabledDates instead
   * disabledDates supports Matcher
   */
  maxDate?: Date | null;
  /**
   * @deprecated use disabledDates instead
   * disabledDates supports Matcher
   */
  filterDate?: (date: Date) => boolean;
  /**
   * Helps to disable dates in various ways
   * Look at https://daypicker.dev/api/type-aliases/Matcher
   */
  disabledDates?: Matcher;
  /**
   * @deprecated use onApply instead
   */
  onClickOutside?: () => void;
  /**
   * If true, the portal will be disabled. Props for the popper component that helps to control the position of the calendar (also works with Modal Dialog)
   */
  disablePortal?: boolean;
  /**
   * Callback for canceling the datepicker
   */
  onCancel?: () => void;
  /**
   * Prop that turn off 'close' icon button in input.
   * @default true
   */
  clearable?: boolean;
  /**
   * Callback that adds extra logic to `close` icon button in input.
   */
  onClear?: () => void;
}

/**
 * Datepicker component
 * This wrapper is temporary and will be removed after the migration to the new datepicker is completed
 * the criteria of removal is ensure that all deprecated props are removed
 */
export default function DatePicker(props: DatepickerProps) {
  const disabledDatesMinMax =
    props.minDate && props.maxDate
      ? {
          after: props.maxDate,
          before: props.minDate,
        }
      : undefined;

  const disabledMax = props.maxDate ? { after: props.maxDate } : undefined;
  const disabledMin = props.minDate ? { before: props.minDate } : undefined;
  const disableViaFilterDate = props.filterDate
    ? (date: Date) => !props.filterDate?.(date)
    : undefined;
  return (
    <DatePickerV3
      {...props}
      isOpen={props.isOpen || props.open}
      disablePortal={props.disablePortal}
      selected={props.selected}
      className={props.className}
      eventDates={props.eventDates}
      color={props.color}
      label={props.label}
      fullWidth={props.fullWidth}
      helperText={props.helperText}
      error={props.error}
      infoText={props.infoText}
      disabled={props.disabled}
      // the order is important
      disabledDates={
        props.disabledDates ||
        // // it is important to backward compatibility with old datepicker
        disableViaFilterDate ||
        disabledDatesMinMax ||
        disabledMax ||
        disabledMin
      }
      InfoTextTooltipProps={props.InfoTextTooltipProps}
      warning={props.warning}
      readOnly={props.readOnly}
      size={props.size}
      placement={props.placement}
      onCancel={props.onCancel}
      onApply={(date) => {
        props.onChange(date ?? null);
        props.onCalendarClose?.();
        props.onClickOutside?.();
        props?.onApply?.(date);
      }}
      clearable={props.clearable}
      onClear={props.onClear}
    />
  );
}
